import {
  Box,
  Checkbox,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import type { FC } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { formatAssociatedTestTemplateLimits } from "../../../TestTemplates/utils"
import type { ProductTestFormValues } from "../../schemas"

import { ProductTestResultValueField } from "./ProductTestResultValueField"

import { ColoredLabel } from "@/components/ColoredLabel"
import { FIELD_ERROR_CLASSES, TEXT_AREA_STYLING_PROPS } from "@/constants/forms"
import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"
import {
  getTestTemplateComparisonLabel,
  getTestTemplateResultUomLabel,
} from "@/screens/ManageProducts/components/TestTemplates/utils"
import type { ProductTestWithDocuments } from "@/screens/Products/components/Product/Quality/TestResults/types"
import colors from "@/styles/colors"
import { booleanToYesNoString } from "@/utils/utils"

interface ProductTestResultsFormProps {
  associatedTestTemplates: AssociatedTestTemplate[]
  productTest: ProductTestWithDocuments | null
  isReadOnly: boolean
}

export const ProductTestResultsForm: FC<ProductTestResultsFormProps> = ({
  associatedTestTemplates,
  productTest,
  isReadOnly,
}) => {
  const { control } = useFormContext<ProductTestFormValues>()
  const resolvedAssociatedTestTemplates =
    productTest?.results?.map((result) => result.associatedTestTemplate!) ?? associatedTestTemplates
  const filteredProductTestResults = productTest?.results?.filter((result) => result.associatedTestTemplate!)

  return (
    <Box component='fieldset'>
      <legend className='font-medium tracking-[0.2px] text-gray-700'>Add Test Results</legend>
      <Divider className='mb-6 mt-1' />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className='w-[84px] bg-primary-100'>Test #</TableCell>
            <TableCell className='w-[156px] bg-primary-100'>Description</TableCell>
            <TableCell className='bg-primary-100'>Comparison</TableCell>
            <TableCell className='bg-primary-100'>Limit(s)</TableCell>
            <TableCell className='bg-primary-100'>Results UoM</TableCell>
            <TableCell className='w-[132px] bg-primary-100'>Results</TableCell>
            <TableCell className='w-[132px] bg-primary-100'>Print to COA</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {resolvedAssociatedTestTemplates.map((associatedTestTemplate, index) => (
            <TableRow key={associatedTestTemplate.associatedTestTemplateId}>
              <TableCell>{associatedTestTemplate.sequenceNumber}</TableCell>
              <TableCell>{associatedTestTemplate.testTemplate!.description}</TableCell>
              <TableCell>{getTestTemplateComparisonLabel(associatedTestTemplate.testTemplate!.comparison)}</TableCell>
              <TableCell>{formatAssociatedTestTemplateLimits(associatedTestTemplate)}</TableCell>
              <TableCell>
                {getTestTemplateResultUomLabel(associatedTestTemplate.testTemplate!.resultsUnitOfMeasure)}
              </TableCell>
              <TableCell>
                {isReadOnly ? (
                  <ColoredLabel
                    label={filteredProductTestResults?.[index].value || "-"}
                    backgroundColor={colors.primary["100"]}
                  />
                ) : (
                  <ProductTestResultValueField
                    comparison={associatedTestTemplate!.testTemplate!.comparison}
                    index={index}
                  />
                )}
              </TableCell>
              <TableCell>
                {isReadOnly ? (
                  booleanToYesNoString(associatedTestTemplate.printToAkrochem)
                ) : (
                  <Box className='flex justify-center'>
                    <Controller
                      name={`results.${index}.printToCoa`}
                      control={control}
                      render={({ field }) => <Checkbox {...field} checked={field.value} aria-label='Print to CoA' />}
                    />
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Paper variant='outlined' elevation={0} className='mt-6 border-gray-300 p-6'>
        <Typography variant='subtitle1' component='h2' className='mb-3 mr-auto text-ml text-gray-900'>
          Comments
        </Typography>
        <Controller
          name='comment'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                {...TEXT_AREA_STYLING_PROPS}
                minRows={2}
                placeholder='Add a comment...'
                error={!!error}
                data-testid='comment-input'
              />
              {error && (
                <p className={FIELD_ERROR_CLASSES} data-testid='comment-error'>
                  {error.message}
                </p>
              )}
            </>
          )}
        />
      </Paper>
    </Box>
  )
}
